import { target, targetable } from '@github/catalyst/lib/targetable'
import { cropCanvasAndExportToPNG } from '../submission_form/crop_canvas'

export default targetable(class extends HTMLElement {
  static [target.static] = ['canvas', 'input', 'button', 'fileInput', 'clear']

  async connectedCallback () {
    const scale = 3

    this.canvas.width = this.canvas.parentNode.clientWidth * scale
    this.canvas.height = this.canvas.parentNode.clientHeight * scale

    this.canvas.getContext('2d').scale(scale, scale)

    const { default: SignaturePad } = await import('signature_pad')

    this.pad = new SignaturePad(this.canvas, {
      backgroundColor: 'rgba(255,255,255,0)',
      penColor: '#000000',
      minWidth: 1,
      maxWidth: 2.5
    })

    this.pad.addEventListener('endStroke', () => {
      this.hasDrawing = true
      this.updateButtonState()
      this.hidePlaceholder()
    })

    this.pad.addEventListener('beginStroke', () => {
      this.hidePlaceholder()
    })

    // Handle clear button
    if (this.clear) {
      this.clear.addEventListener('click', (e) => {
        e.preventDefault()
        this.pad.clear()
        this.hasDrawing = false
        this.uploadedFile = null
        this.showPlaceholder()
        this.updateButtonState()
      })
    }

    // Handle file upload from dropdown
    if (this.fileInput) {
      this.fileInput.addEventListener('change', (e) => {
        const file = e.target.files[0]
        if (file) this.uploadFile(file)
      })
    }

    this.button.addEventListener('click', (e) => {
      e.preventDefault()

      const hasContent = this.hasDrawing || this.uploadedFile
      if (!hasContent) {
        this.toggleDropdown()
        return
      }

      this.button.disabled = true
      this.submit()
    })

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeDropdown()
      }
    })
  }

  hidePlaceholder () {
    const placeholder = this.querySelector('[data-signature-placeholder]')
    const preview = this.querySelector('[data-signature-preview]')
    if (placeholder) placeholder.classList.add('hidden')
    if (preview) preview.classList.add('hidden')
  }

  showPlaceholder () {
    const placeholder = this.querySelector('[data-signature-placeholder]')
    if (placeholder) placeholder.classList.remove('hidden')
  }

  toggleDropdown () {
    const menu = this.querySelector('[data-signature-dropdown-menu]')
    if (menu) menu.classList.toggle('hidden')
  }

  closeDropdown () {
    const menu = this.querySelector('[data-signature-dropdown-menu]')
    if (menu) menu.classList.add('hidden')
  }

  updateButtonState () {
    const hasContent = this.hasDrawing || this.uploadedFile
    if (hasContent) {
      this.button.classList.remove('opacity-50', 'cursor-not-allowed')
      this.button.classList.add('hover:bg-[#09696e]')
    } else {
      this.button.classList.add('opacity-50', 'cursor-not-allowed')
      this.button.classList.remove('hover:bg-[#09696e]')
    }
  }

  async uploadFile (file) {
    this.uploadedFile = file
    this.hasDrawing = false
    this.pad.clear()

    const ctx = this.canvas.getContext('2d')
    ctx.clearRect(0, 0, this.canvas.width / 3, this.canvas.height / 3)

    const img = new Image()
    img.onload = () => {
      const cw = this.canvas.width / 3
      const ch = this.canvas.height / 3
      const s = Math.min(cw / img.width, ch / img.height, 1)
      const x = (cw - img.width * s) / 2
      const y = (ch - img.height * s) / 2
      ctx.drawImage(img, x, y, img.width * s, img.height * s)
    }
    img.src = URL.createObjectURL(file)

    this.hidePlaceholder()
    this.updateButtonState()
    this.closeDropdown()
  }

  async submit () {
    let file

    if (this.uploadedFile) {
      file = this.uploadedFile
    } else {
      const blob = await cropCanvasAndExportToPNG(this.canvas)
      file = new File([blob], 'signature.png', { type: 'image/png' })
    }

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    this.input.files = dataTransfer.files

    this.closest('form').requestSubmit()
  }
})
